/**
 * Saved Leads — the sourcing pipeline workspace (Stage Z PR C).
 *
 * Renders PR B's shared `ProductComparisonCard`, so a saved lead looks identical
 * to the Auto Sourcing card it came from. Everything on this page is driven by
 * persisted data: counts come from the lead rows, collections from the API, and
 * status/notes write back with confirmation before the UI updates.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Bookmark, Download, Folder, LayoutGrid, Loader2, Plus, Rows3, Search, X,
} from "lucide-react";
import { PageHeader } from "@/components/shell/PageHeader";
import { ProductComparisonCard } from "@/components/product-card/ProductComparisonCard";
import { OpportunityDetailDialog } from "@/components/deal-flow/OpportunityDetailDialog";
import { CollectionForm } from "@/components/leads/CollectionPicker";
import { api } from "@/lib/api";
import {
  LEAD_STATUSES,
  createCollection,
  deleteCollection,
  listCollectionItems,
  listCollections,
  updateCollection,
  updateLead,
} from "@/lib/leads/collectionsApi";
import { mapSavedLeadsToCards } from "@/lib/leads/savedLeadAdapter";
import { unsaveLead } from "@/lib/leads/savedLeads";
import { retailerName } from "@/lib/leads/retailerIdentity";
import { cn } from "@/lib/utils";
import "@/styles/saved-leads.css";

const PAGE_SIZE = 24;

/**
 * Combine the two histories into one chart row set.
 *
 * Walmart snapshots carry BOTH prices at one instant, so they are passed through
 * as-is. Source observations are single-price readings from a different series and
 * are appended as their own rows. They are NOT merged by timestamp: two readings
 * at the same minute are still two distinct observations, and collapsing them
 * would invent a price neither source reported.
 */
function mergeSeriesRows(data) {
  const walmart = Array.isArray(data?.items) ? data.items : [];
  const source = Array.isArray(data?.source_items)
    ? data.source_items.map((row) => ({
        observed_at: row.observed_at,
        source_price: row.price,
      }))
    : [];
  return [...walmart, ...source];
}

const STATUS_FILTERS = [{ key: "all", label: "All" }, ...LEAD_STATUSES];

const QUALIFICATION_FILTERS = [
  { key: "all", label: "All verdicts" },
  { key: "QUALIFIED", label: "Qualified" },
  { key: "UNPROFITABLE", label: "Unprofitable" },
  { key: "IDENTITY_REJECTED", label: "Identity rejected" },
  { key: "INSUFFICIENT_EVIDENCE", label: "Insufficient evidence" },
  { key: "NO_MATCH", label: "No match" },
];

const SORTS = [
  { key: "saved", label: "Recently saved" },
  { key: "updated", label: "Recently updated" },
  { key: "profit", label: "Highest profit" },
  { key: "roi", label: "Highest ROI" },
  { key: "cost", label: "Lowest source cost" },
  { key: "title", label: "Product A–Z" },
];

const COLOR_DOT = {
  emerald: "bg-emerald-500", blue: "bg-sky-500", amber: "bg-amber-500",
  rose: "bg-rose-500", violet: "bg-violet-500", slate: "bg-slate-500", teal: "bg-teal-500",
};

const money = (v) =>
  v == null || !Number.isFinite(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Metric({ label, value, tone = "", testId }) {
  return (
    <div className="as-sl-metric" data-testid={testId}>
      <span className="as-sl-metric-label">{label}</span>
      <span className={cn("as-sl-metric-value tabular-nums", tone)}>{value}</span>
    </div>
  );
}

/** Status + note editing for one lead. Writes only after backend confirmation. */
export function LeadStatusEditor({ lead, onSaved }) {
  const [status, setStatus] = useState(lead.leadStatus || "sourcing");
  const [note, setNote] = useState(lead.note || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus(lead.leadStatus || "sourcing");
    setNote(lead.note || "");
  }, [lead.id, lead.leadStatus, lead.note]);

  const save = async (patch) => {
    setBusy(true);
    try {
      await updateLead({
        walmartItemId: lead.walmartItemId,
        sourceKey: lead.sourceKey,
        ...patch,
      });
      onSaved?.(patch);
      toast.success("Lead updated.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail.replace(/_/g, " ") : "Could not update this lead.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="as-sl-lead-controls" data-testid={`lead-controls-${lead.id}`}>
      <label className="as-sl-control">
        <span>Status</span>
        <select
          value={status}
          disabled={busy}
          data-testid={`lead-status-${lead.id}`}
          onChange={(e) => {
            const next = e.target.value;
            setStatus(next);
            void save({ status: next });
          }}
        >
          {LEAD_STATUSES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </label>
      <label className="as-sl-control as-sl-note">
        <span>Note</span>
        <input
          value={note}
          maxLength={2000}
          disabled={busy}
          placeholder="Add a note…"
          data-testid={`lead-note-${lead.id}`}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if ((lead.note || "") !== note) void save({ note });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </label>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-muted" /> : null}
    </div>
  );
}

export default function SavedLeads() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState(null);
  const [status, setStatus] = useState("all");
  const [qualification, setQualification] = useState("all");
  const [retailer, setRetailer] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("saved");
  const [density, setDensity] = useState("comfortable");
  const [page, setPage] = useState(1);
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [busyCollection, setBusyCollection] = useState(false);

  // ── Product detail popup ──────────────────────────────────────────────────
  // Reuses the ONE dialog Auto Sourcing uses. Saved Leads previously rendered
  // cards with no `onOpen`, so the card was inert: its click handler existed but
  // nothing listened. The dialog is driven entirely by the persisted lead, never
  // by a live run, and history is resolved from the lead's own identity.
  const [detailIndex, setDetailIndex] = useState(null);
  const [detailFocus, setDetailFocus] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailCoverage, setDetailCoverage] = useState(null);

  const loadLeads = useCallback(async () => {
    setError(null);
    try {
      const res = await api.leads();
      const items = res?.data || [];
      setRows(Array.isArray(items) ? items : []);
    } catch {
      setRows([]);
      setError("Could not load your saved leads.");
    }
  }, []);

  const loadCollections = useCallback(async () => {
    try {
      const res = await listCollections();
      setCollections(res?.data?.items || res?.items || []);
    } catch {
      setCollections([]);
    }
  }, []);

  useEffect(() => {
    void loadLeads();
    void loadCollections();
  }, [loadLeads, loadCollections]);

  // Cross-page sync: the card and popup pickers dispatch this after a confirmed
  // mutation, so folder counts and memberships update without a reload.
  useEffect(() => {
    const onChanged = () => {
      void loadLeads();
      void loadCollections();
    };
    window.addEventListener("izisell:collections-changed", onChanged);
    return () => window.removeEventListener("izisell:collections-changed", onChanged);
  }, [loadLeads, loadCollections]);

  const cards = useMemo(() => mapSavedLeadsToCards(rows || []), [rows]);

  // ── Summary metrics: derived from real rows, never hardcoded ──────────────
  const summary = useMemo(() => {
    const total = cards.length;
    const byStatus = Object.fromEntries(LEAD_STATUSES.map((s) => [s.key, 0]));
    let potential = 0;
    let potentialCount = 0;
    for (const c of cards) {
      const key = c.leadStatus || "sourcing";
      byStatus[key] = (byStatus[key] || 0) + 1;
      // Potential Profit counts ONLY qualified, fee-covered, positive profit.
      if (
        String(c.qualification || "").toUpperCase() === "QUALIFIED" &&
        !c.identityRejected &&
        Number(c.profit) > 0
      ) {
        potential += Number(c.profit);
        potentialCount += 1;
      }
    }
    return { total, byStatus, potential, potentialCount };
  }, [cards]);

  const retailers = useMemo(() => {
    const seen = new Map();
    for (const c of cards) {
      if (c.sourceId && !seen.has(c.sourceId)) {
        seen.set(c.sourceId, retailerName(c.sourceId));
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [cards]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = cards.filter((c) => {
      if (status !== "all" && (c.leadStatus || "sourcing") !== status) return false;
      if (qualification !== "all") {
        const v = String(c.qualification || "").toUpperCase();
        if (v !== qualification) return false;
      }
      if (retailer !== "all" && c.sourceId !== retailer) return false;
      if (q) {
        const hay = [c.sourceTitle, c.walmartTitle, c.sourceBrand, c.walmartBrand]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const cmp = {
      saved: (a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
      updated: (a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
      profit: (a, b) => (Number(b.profit) || -Infinity) - (Number(a.profit) || -Infinity),
      roi: (a, b) => (Number(b.roi) || -Infinity) - (Number(a.roi) || -Infinity),
      cost: (a, b) => (Number(a.sourcePrice) ?? Infinity) - (Number(b.sourcePrice) ?? Infinity),
      title: (a, b) => String(a.sourceTitle || "").localeCompare(String(b.sourceTitle || "")),
    }[sort];
    if (cmp) out = out.slice().sort(cmp);
    return out;
  }, [cards, status, qualification, retailer, query, sort]);

  /**
   * Open the shared product-detail popup for one saved lead.
   *
   * History is resolved from the PERSISTED lead identity (Walmart item + retailer
   * offer), never from a live run, so it still works after the sourcing job that
   * produced the lead is gone. A history failure is not fatal: the dialog opens
   * with an honest "no history yet" state rather than refusing to open.
   *
   * `filtered` is a dependency because the dialog navigates within the list the
   * user is currently looking at; capturing a stale list would page to a product
   * that is no longer on screen.
   */
  /**
   * Bookmark on this page can only mean UNSAVE — every lead listed here is
   * already saved. Without a handler the card called `onSave` unconditionally and
   * threw `onSave is not a function`, which is why the bookmark was dead.
   *
   * It stops propagation inside the card, so it can never open the popup.
   */
  const handleToggleSave = useCallback(
    async (lead) => {
      if (!lead?.walmartItemId) return;
      try {
        await unsaveLead({
          walmartItemId: lead.walmartItemId,
          sourceKey:
            lead.sourceId && lead.sourceProductId
              ? `${lead.sourceId}:${lead.sourceProductId}`
              : "",
        });
        toast.success("Removed from saved leads");
        await loadLeads();
      } catch {
        toast.error("Could not update this lead");
      }
    },
    [loadLeads]
  );

  const openDetail = useCallback((lead, focus = null) => {
    const idx = filtered.findIndex((l) => l.id === lead?.id);
    if (idx < 0) return;
    setDetailIndex(idx);
    setDetailFocus(focus);
    setDetailHistory([]);
    api
      .savedLeadHistory({
        walmartItemId: lead?.walmartItemId,
        sourceId: lead?.sourceId,
        sourceProductId: lead?.sourceProductId,
      })
      .then((res) => {
        const data = res?.data ?? res ?? {};
        setDetailHistory(mergeSeriesRows(data));
        setDetailCoverage(data?.coverage_meta?.walmart || null);
      })
      .catch(() => setDetailHistory([]));
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const activeCollection = collections.find((c) => c.collection_id === collectionId) || null;

  const applyLeadPatch = (leadId, patch) => {
    setRows((prev) =>
      (prev || []).map((r, i) => {
        const mapped = mapSavedLeadsToCards([r])[0];
        if (!mapped || (mapped.id !== leadId)) return r;
        return { ...r, ...patch };
      }),
    );
  };

  const handleCreateCollection = async (fields) => {
    setBusyCollection(true);
    try {
      const res = await createCollection(fields);
      const created = res?.data?.item || res?.item;
      if (created) {
        toast.success(`Collection “${created.name}” created.`);
        await loadCollections();
      }
      setShowCollectionForm(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail.replace(/_/g, " ") : "Could not create the collection.");
    } finally {
      setBusyCollection(false);
    }
  };

  const handleRenameCollection = async (fields) => {
    if (!editingCollection) return;
    setBusyCollection(true);
    try {
      await updateCollection(editingCollection.collection_id, fields);
      toast.success("Collection updated.");
      setEditingCollection(null);
      await loadCollections();
    } catch {
      toast.error("Could not update the collection.");
    } finally {
      setBusyCollection(false);
    }
  };

  const handleDeleteCollection = async (col) => {
    try {
      await deleteCollection(col.collection_id);
      toast.success(`“${col.name}” deleted. Its leads were kept.`);
      if (collectionId === col.collection_id) setCollectionId(null);
      await loadCollections();
      await loadLeads();
    } catch {
      toast.error("Could not delete the collection.");
    }
  };

  const exportCsv = () => {
    const header = [
      "Source retailer", "Source title", "Source price",
      "Walmart item", "Walmart title", "Walmart price",
      "Spread", "Profit", "ROI", "Margin", "Qualification", "Status", "Note",
    ];
    const line = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = filtered.map((c) => [
      retailerName(c.sourceId), c.sourceTitle, c.sourcePrice,
      c.walmartItemId, c.walmartTitle, c.walmartPrice,
      c.spread, c.profit, c.roi, c.margin, c.qualification, c.leadStatus, c.note,
    ].map(line).join(","));
    const blob = new Blob([[header.map(line).join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "saved-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const loading = rows === null;

  return (
    <div className="as-sl-page" data-testid="saved-leads-page">
      <PageHeader
        title="Saved Leads"
        subtitle="Save, organize, review, and manage products for your Walmart sourcing business."
        actions={
          <div className="flex items-center gap-2">
            {/* No "New Lead" button: creating a lead without a source product has
                no supported data model, so it would be a dead control. */}
            <button
              type="button"
              onClick={() => setShowCollectionForm(true)}
              data-testid="new-collection-button"
              className="as-sl-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New Collection
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!filtered.length}
              data-testid="export-leads-button"
              className="as-sl-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          </div>
        }
      />

      {/* ── Summary ─────────────────────────────────────────────────────── */}
      <div className="as-sl-summary" data-testid="saved-leads-summary">
        <Metric label="Total Leads" value={summary.total} testId="sl-metric-total" />
        <Metric label="Sourcing" value={summary.byStatus.sourcing} testId="sl-metric-sourcing" />
        <Metric label="Under Review" value={summary.byStatus.review} testId="sl-metric-review" />
        <Metric label="Watching" value={summary.byStatus.watch} testId="sl-metric-watch" />
        <Metric label="Purchased" value={summary.byStatus.purchased} testId="sl-metric-purchased" />
        <Metric label="Archived" value={summary.byStatus.archived} testId="sl-metric-archived" />
        <div className="as-sl-metric" data-testid="sl-metric-potential-profit">
          <span className="as-sl-metric-label">Potential Profit</span>
          <span className="as-sl-metric-value tabular-nums text-emerald-700">
            {money(summary.potential)}
          </span>
          <span className="as-sl-metric-hint">
            Qualified leads only ({summary.potentialCount}); estimates exclude unverified deals.
          </span>
        </div>
      </div>

      {/* ── Collections as folders ──────────────────────────────────────── */}
      <div className="as-sl-collections" data-testid="saved-leads-collections">
        <button
          type="button"
          onClick={() => setCollectionId(null)}
          className={cn("as-sl-outline-chip", !collectionId && "is-active")}
          data-testid="collection-folder-all"
        >
          <Folder className="w-3.5 h-3.5" /> All leads
          <span className="as-sl-chip-count">{summary.total}</span>
        </button>
        {collections.map((col) => (
          <span key={col.collection_id} className="as-sl-folder-wrap">
            <button
              type="button"
              onClick={() => setCollectionId(col.collection_id)}
              className={cn("as-sl-outline-chip", collectionId === col.collection_id && "is-active")}
              data-testid={`collection-folder-${col.collection_id}`}
            >
              <span className={cn("as-sl-color-dot", COLOR_DOT[col.color] || COLOR_DOT.slate)} />
              {col.name}
              <span className="as-sl-chip-count">{col.item_count ?? 0}</span>
            </button>
            <button
              type="button"
              aria-label={`Rename ${col.name}`}
              data-testid={`collection-edit-${col.collection_id}`}
              onClick={() => setEditingCollection(col)}
              className="as-sl-chip-action"
            >
              ✎
            </button>
            <button
              type="button"
              aria-label={`Delete ${col.name}`}
              data-testid={`collection-delete-${col.collection_id}`}
              onClick={() => handleDeleteCollection(col)}
              className="as-sl-chip-action"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setShowCollectionForm(true)}
          className="as-sl-outline-chip as-sl-chip-dashed"
          data-testid="collection-folder-new"
        >
          <Plus className="w-3.5 h-3.5" /> New folder
        </button>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="as-sl-toolbar">
        <div className="as-sl-statuses">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => { setStatus(f.key); setPage(1); }}
              aria-pressed={status === f.key}
              className={cn("as-sl-status", status === f.key && "is-active")}
              data-testid={`sl-status-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="as-sl-filters">
          <label className="as-sl-search">
            <Search className="w-3.5 h-3.5" />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search product, brand…"
              data-testid="sl-search"
            />
          </label>
          <select
            value={retailer}
            onChange={(e) => { setRetailer(e.target.value); setPage(1); }}
            data-testid="sl-retailer-filter"
            aria-label="Filter by retailer"
          >
            <option value="all">All retailers</option>
            {retailers.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <select
            value={qualification}
            onChange={(e) => { setQualification(e.target.value); setPage(1); }}
            data-testid="sl-qualification-filter"
            aria-label="Filter by verdict"
          >
            {QUALIFICATION_FILTERS.map((q) => (
              <option key={q.key} value={q.key}>{q.label}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sl-sort"
            aria-label="Sort"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <div className="as-sl-density">
            <button
              type="button"
              onClick={() => setDensity("comfortable")}
              aria-pressed={density === "comfortable"}
              data-testid="sl-density-comfortable"
              aria-label="Comfortable view"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setDensity("compact")}
              aria-pressed={density === "compact"}
              data-testid="sl-density-compact"
              aria-label="Compact view"
            >
              <Rows3 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Collection scope banner ─────────────────────────────────────── */}
      {activeCollection ? (
        <div className="as-sl-scope" data-testid="sl-collection-scope">
          <Folder className="w-3.5 h-3.5" />
          <strong>{activeCollection.name}</strong>
          <span className="text-ink-muted">
            {activeCollection.item_count ?? 0} product
            {(activeCollection.item_count ?? 0) === 1 ? "" : "s"}
          </span>
          {activeCollection.description ? (
            <span className="text-ink-muted">· {activeCollection.description}</span>
          ) : null}
          <button type="button" onClick={() => setCollectionId(null)} className="as-sl-scope-clear">
            Clear
          </button>
        </div>
      ) : null}

      {/* ── Grid ────────────────────────────────────────────────────────── */}
      {error ? (
        <div className="as-sl-empty" data-testid="saved-leads-error">
          <p>{error}</p>
          <button type="button" onClick={() => void loadLeads()} className="as-sl-btn">Retry</button>
        </div>
      ) : loading ? (
        <div className="as-sl-empty" data-testid="saved-leads-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading saved leads…
        </div>
      ) : filtered.length === 0 ? (
        <div className="as-sl-empty" data-testid="saved-leads-empty">
          <Bookmark className="w-6 h-6 text-ink-muted" />
          <p className="font-semibold text-ink-primary">
            {cards.length === 0
              ? "No saved leads yet."
              : "No leads match these filters."}
          </p>
          <p className="text-ink-muted">
            {cards.length === 0
              ? "Save a product from Auto Sourcing to start your sourcing pipeline."
              : "Try clearing filters or a different collection."}
          </p>
          {cards.length === 0 ? (
            <a href="/app/research/auto-sourcing" className="as-sl-btn">
              Go to Auto Sourcing
            </a>
          ) : null}
        </div>
      ) : (
        <div
          className={cn("as-sl-grid", density === "compact" && "is-compact")}
          data-testid="saved-leads-grid"
        >
          {paged.map((lead) => (
            <div key={lead.id} className="as-sl-cell">
              <ProductComparisonCard
                lead={lead}
                variant={density === "compact" ? "compact" : "comfortable"}
                // The card's click handlers already exist and already stop
                // propagation for independent actions (bookmark, folder, status,
                // note). They were inert here only because nothing was wired.
                onSave={handleToggleSave}
                onOpen={(l) => openDetail(l, null)}
                onAnalyze={(l) => openDetail(l, "analysis")}
              />
              <LeadStatusEditor
                lead={lead}
                onSaved={(patch) => applyLeadPatch(lead.id, {
                  ...(patch.status ? { status: patch.status } : {}),
                  ...(patch.note !== undefined ? { note: patch.note } : {}),
                })}
              />
            </div>
          ))}
        </div>
      )}

      {detailIndex != null && filtered[detailIndex] ? (
        <OpportunityDetailDialog
          lead={filtered[detailIndex]}
          list={filtered}
          index={detailIndex}
          onIndex={(i) => {
            const next = filtered[i];
            setDetailIndex(i);
            setDetailHistory([]);
            if (next) {
              api
                .savedLeadHistory({
                  walmartItemId: next.walmartItemId,
                  sourceId: next.sourceId,
                  sourceProductId: next.sourceProductId,
                })
                .then((res) => {
                  const data = res?.data ?? res ?? {};
                  setDetailHistory(mergeSeriesRows(data));
                  setDetailCoverage(data?.coverage_meta?.walmart || null);
                })
                .catch(() => setDetailHistory([]));
            }
          }}
          onClose={() => {
            // Closing must not disturb filters, folder selection or paging.
            setDetailIndex(null);
            setDetailFocus(null);
            setDetailHistory([]);
            setDetailCoverage(null);
          }}
          focus={detailFocus}
          history={detailHistory}
          coverageMeta={detailCoverage}
        />
      ) : null}

      {totalPages > 1 ? (
        <div className="as-sl-pager" data-testid="saved-leads-pager">
          <button type="button" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
            Previous
          </button>
          <span>Page {safePage} of {totalPages}</span>
          <button type="button" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>
            Next
          </button>
        </div>
      ) : null}

      {/* ── Create / edit collection modal ──────────────────────────────── */}
      {(showCollectionForm || editingCollection) ? (
        <div className="as-sl-modal-backdrop" data-testid="collection-modal">
          <div className="as-sl-modal" role="dialog" aria-modal="true">
            <h3>{editingCollection ? "Edit collection" : "New collection"}</h3>
            <CollectionForm
              busy={busyCollection}
              onCancel={() => { setShowCollectionForm(false); setEditingCollection(null); }}
              onCreate={(fields) =>
                editingCollection ? handleRenameCollection(fields) : handleCreateCollection(fields)
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
